import {
	AccordionSection,
	LabeledCheckbox,
	MedLargeStepWrapper,
	MuiAlert,
	MuiAlertTitle,
	MuiMenuItem,
	NumberInput,
	Paper,
	Select,
	Typography
} from '@fox/atoms';
import { FlexColumnSpaced, SpaceBetweenRow } from '@fox/layout';
import { useMemo } from 'react';
import styled from 'styled-components';
import { ImportDataGrid } from './data-grid';
import { MapOption, useImportWizardContext } from './import-wizard';
import { ProfileSection } from './profile-section';
import { checkRequiredFields, tryMatchColumns } from './sheet-utils';
import { ImportProfile, MatchOption } from './types';

const MappingOption = styled.div`
	padding: 0.5rem;
	gap: 1rem;
	height: auto;
`;

export const MatchColumnsLargeStep = () => (
	<MedLargeStepWrapper>
		<MatchColumns />
	</MedLargeStepWrapper>
);

function CustomMatchFields() {
	const {
		state: { customMatchKeys, matchAlgorithm },
		dispatch,
		mapTypes
	} = useImportWizardContext() ?? {};

	const matchableFields = mapTypes.filter(a => !!a.match);

	return (
		<Select<MapOption, true, false, false>
			options={matchableFields}
			multiple
			label="Match Fields"
			onChange={(_, val) => dispatch({ type: 'SET_SEARCH', value: matchAlgorithm, custom: val.map(a => a.key) })}
			getOptionLabel={a => a.title}
			value={matchableFields.filter(a => customMatchKeys?.some(n => n === a.key) ?? false) ?? []}
		/>
	);
}

const CUSTOM_MATCH = 'custom';

export const MatchColumns = () => {
	const {
		state: { sheet, fileData: data, matchAlgorithm, customMatchKeys },
		dispatch,
		matchOptions,
		mapRef,
		setMapping,
		mapTypes,
		profile,
		setProfile,
		createImportProfile,
		deleteImportProfile,
		getImportProfiles
	} = useImportWizardContext() ?? {};

	const enableProfileSettings = createImportProfile && deleteImportProfile && getImportProfiles;

	// important - this useMemo will ensure the object defining access to the sheet will remain the same
	// data.workbook should be a static reference
	const ws = useMemo(() => data?.workbook?.GetSheet(sheet), [data?.workbook, sheet]);

	const algo = matchOptions?.find(o => o.id === matchAlgorithm)?.requiredFields;

	const isCustomMatch = matchAlgorithm === CUSTOM_MATCH;

	const isError =
		algo && mapRef ? !checkRequiredFields(isCustomMatch && customMatchKeys ? algo.concat(customMatchKeys) : algo, mapRef) : false;
	const matchOptionsDisplay = matchOptions.find(f => f.id === matchAlgorithm);

	const displayText = (option: { label: string; id: string; requiredFields: MatchOption['requiredFields'] }) => {
		const customExtra = isCustomMatch && customMatchKeys ? mapTypes.filter(t => customMatchKeys.includes(t.key)).map(a => a.title) : [];
		const joinedFieldsDisplay = option.requiredFields
			.map(r => {
				const mapType = mapTypes.find(m => m.key === r);
				return mapType?.title ?? r;
			})
			.concat(customExtra)
			.map(field => {
				if (Array.isArray(field)) {
					return `(any of ${field.join(', ')})`;
				} else {
					return field;
				}
			})
			.join(', ');

		return joinedFieldsDisplay;
	};

	function updateHeaderSettings(change: Partial<Pick<ImportProfile, 'hasHeader' | 'skipRowsStart'>>) {
		const updated = { ...profile, ...change };
		const header = ws.extractHeader(updated.hasHeader, updated.skipRowsStart);

		for (const mapping in mapRef) {
			setMapping(mapping, null);
		}

		dispatch({ type: 'SET_HAS_HEADER' });
		setProfile(updated);
		tryMatchColumns(header, setMapping, mapTypes, mapRef, profile);
	}

	return (
		<>
			<FlexColumnSpaced>
				<Typography variant="h2">Match your spreadsheet to SwiftFox</Typography>
				<AccordionSection
					limitedWidth={true}
					defaultExpanded={true}
					details={
						<>
							<Typography variant="h6">What spreadsheet do you wish to import?</Typography>
							<Select
								value={sheet}
								onChange={(_, v) => {
									const ws = data?.workbook?.GetSheet(v);
									const header = ws.extractHeader(true);
									for (const mapping in mapRef) {
										setMapping(mapping, null);
									}
									dispatch({ type: 'SELECT_SHEET', name: v });
									tryMatchColumns(header, setMapping, mapTypes, mapRef, profile);
								}}
								options={data?.workbook?.SheetNames}
								size="small"
								label="Select spreadsheet"
								fullWidth
								disableClearable={true}
							/>
							<Typography variant="h6">How should we match your import to existing SwiftFox records?</Typography>
							<Select<
								{ label: string; description?: string; id: string; requiredFields: MatchOption['requiredFields'] },
								false,
								true,
								false
							>
								value={matchOptions.find(f => f.id === matchAlgorithm) ?? null}
								onChange={(a, v) => {
									dispatch({ type: 'SET_SEARCH', value: v?.id });
								}}
								options={matchOptions}
								fullWidth
								getOptionLabel={o => {
									if (typeof o === 'string') {
										return o;
									} else {
										return o.label;
									}
								}}
								size="small"
								label="Select how we match"
								disableClearable={true}
								PaperComponent={({ children }) => {
									return <Paper style={{ minWidth: '170%' }}>{children}</Paper>;
								}}
								renderOption={(props, option) => {
									const subTitle = option.description
										? option.description
										: option.requiredFields.length > 0
										? ` Requires your data to match Swiftfox ${displayText(option)} fields.`
										: 'No required fields.';

									return (
										<MuiMenuItem {...props} key={option.id}>
											<MappingOption>
												<Typography>{option.label}</Typography>
												<Typography variant="caption">{subTitle}</Typography>
											</MappingOption>
										</MuiMenuItem>
									);
								}}
							/>
							{matchOptions && matchOptions.length && Array.isArray(matchOptions) && isCustomMatch ? (
								<CustomMatchFields />
							) : null}
						</>
					}
					summaryTitle={`Let's get started`}
				/>
				<AccordionSection
					limitedWidth={true}
					details={
						<>
							<Typography variant="h5">Do you want to skip or limit rows when importing?</Typography>
							<LabeledCheckbox
								label="Is the first row in your spreadsheet a header?"
								checked={profile.hasHeader ?? true}
								value={profile.hasHeader ?? true}
								onChange={(_, checked) => {
									updateHeaderSettings({ hasHeader: checked });
								}}
							/>
							<SpaceBetweenRow>
								<Typography variant="body1">Skip rows</Typography>
								<NumberInput
									min={0}
									label="Number of rows"
									value={profile.skipRowsStart}
									onChange={v => updateHeaderSettings({ skipRowsStart: typeof v === 'number' ? v : null })}
								/>
							</SpaceBetweenRow>
							<SpaceBetweenRow>
								<Typography variant="body1">Skip end rows</Typography>
								<NumberInput
									min={0}
									label="Number of rows"
									value={profile.skipRowsEnd}
									onChange={v => setProfile({ ...profile, skipRowsEnd: typeof v === 'number' ? v : null })}
								/>
							</SpaceBetweenRow>
							<SpaceBetweenRow>
								<Typography variant="body1">Limit import rows</Typography>
								<NumberInput
									min={0}
									label="Number of rows"
									value={profile.importLimit}
									onChange={v => setProfile({ ...profile, importLimit: typeof v === 'number' ? v : null })}
								/>
							</SpaceBetweenRow>
						</>
					}
					summaryTitle={'Additional import options'}
				/>
				{enableProfileSettings ? <ProfileSection isCustomMatch={isCustomMatch} ws={ws} /> : null}
			</FlexColumnSpaced>

			{isError ? (
				<MuiAlert severity="warning">
					<MuiAlertTitle>There are missing required SwiftFox fields.</MuiAlertTitle>
					<Typography variant="body2">
						Action required: If you have chosen the match option: {matchOptionsDisplay?.label}, you are required to match (
						{displayText(matchOptionsDisplay)}) under SwiftFox fields.
					</Typography>
				</MuiAlert>
			) : null}
			{ws ? <ImportDataGrid ws={ws} /> : <MuiAlert color="warning">Select a sheet to import.</MuiAlert>}
		</>
	);
};
